import { Component, Inject, OnInit, signal, OnDestroy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { Daypart, DaypartDay, Ride, Setting, User, Vehicle } from 'src/app/interfaces';
import { CopyDaypartComponent } from '../../dialogs/copy-daypart/copy-daypart.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment-timezone';
import { getDateString, valueToDate } from 'src/app/globals';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  DocumentReference,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QueryConstraint,
  setDoc,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { dataStore, db } from 'src/app/app.component';
import { calculateAutomaticRideOccupancy, getWeekString } from '../../helpers/ride-helper';
import { Role } from 'src/app/enums';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger, MatOption } from '@angular/material/select';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export interface DialogData {
  retourRide: any;
  ride: any;
  start: Date;
  end: Date;
  dayPart: Daypart;
  vehicle: Vehicle;
  dayPartDay: DaypartDay;
  planType: string;
}

export interface PlanningUser {
  id: string;
  user: User;
  priority: number;
  recommended?: boolean;
  available?: boolean;
  ridesVehicle?: boolean;
  hasException?: boolean;
  exceptionTitle?: string;
  alreadyBooked?: boolean;
}

@Component({
  selector: 'app-plan-daypart',
  templateUrl: './plan-daypart.component.html',
  styleUrls: ['./plan-daypart.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatSelectTrigger,
    NgTemplateOutlet,
    MatOption,
    MatSelectSearchComponent,
    MatInput,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatTooltip,
    NgClass,
    MatError,
    MatProgressSpinner,
  ],
})
export class PlanDaypartComponent implements OnInit, OnDestroy {
  daypartDaysForDate: DaypartDay[] = [];
  daypartDaysSub?: Unsubscribe;
  loading = signal(true);
  saving = signal(false);
  isFinished = false;
  newItem = false;
  saveItem: any = {};
  date: Date;
  startString: string;
  organisationId: string = dataStore.selectedOrganisation.id;
  planDayPartDayForm: UntypedFormGroup = this.fb.group({
    customStart: [false],
    startHour: [],
    startMinute: [],
    customEnd: [false],
    endHour: [],
    endMinute: [],
    comment: [],
  });
  searchForm = this.fb.group({
    search: [],
    searchCoDriver: [],
  });

  hours = signal<number[]>([]);
  minutes = signal<any[]>([]);
  minuteStep = 5;
  invalidTimeCombination = signal(false);

  driverEmail: string;
  driverPhone: string;
  coDriverEmail: string;
  coDriverPhone: string;

  allUsers: PlanningUser[];
  totalUsers: number;
  filteredUsers = signal<PlanningUser[]>([]);
  filteredCoDrivers = signal<PlanningUser[]>([]);
  selectedPlannerUsers = signal<any[]>([]);
  selectedPlannerIds = signal<string[]>([]);
  removedPlannerIds = signal<any[]>([]);

  planDayPartDoc: DocumentReference<DocumentData, DocumentData>;
  currentUser = signal<PlanningUser | null>(null);
  currentCoDriver = signal<PlanningUser | null>(null);
  privateVehicleDriverError = signal<string | null>(null);

  moment = moment;
  dataStore = dataStore;
  RoleEnum = Role;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PlanDaypartComponent>,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    console.log('PlanDaypartComponent data', this.data);
    const settings = dataStore.generalSettings as Setting;

    if (!settings.daypartSettings) {
      this.snackBar.open("Er zijn nog geen 'van-tot'-uren ingesteld bij de instellingen.", 'X');
      return;
    }

    const startHourSettings = settings.daypartSettings.startHour;
    const endHourSettings = settings.daypartSettings.endHour;

    for (let i = startHourSettings; i <= endHourSettings; i++) {
      this.hours.update((hours) => [...hours, i]);
    }

    for (let i = 0; i < 60; i += this.minuteStep) {
      this.minutes.update((minutes) => [
        ...minutes,
        {
          val: i,
          text: `0${i}`.slice(-2),
        },
      ]);
    }

    const dialogEnd = valueToDate(this.data.end);
    this.isFinished = !!dialogEnd && moment(dialogEnd).isSameOrBefore(moment());

    if (!this.data.dayPartDay || Object.keys(this.data.dayPartDay).length == 0) {
      this.newItem = true;
      this.date = this.data.start;
      this.startString = getDateString(this.data.start);

      // WARNING
      // The values below should NEVER be changed or they will unlink on the calendar
      this.saveItem.date = this.data.start;
      this.saveItem.startString = getDateString(this.data.start);
      this.saveItem.year = moment(this.data.start).year();
      this.saveItem.month = moment(this.data.start).month();
      this.saveItem.week = getWeekString(this.data.start);
      this.saveItem.day = moment(this.data.start).date();
      this.saveItem.daypartId = this.data.dayPart.id;
      if (this.data.planType === 'drivers') {
        this.saveItem.vehicleId = this.data.vehicle.id;
      }
      // The values above should NEVER be changed or they will unlink on the calendar

      this.saveItem.lastAlerted = null;
    } else {
      this.date = valueToDate(this.data.dayPartDay.date)!;
      this.startString = this.data.dayPartDay.startString;
      const end = valueToDate(this.data.dayPartDay.end);
      this.isFinished = !!end && moment(end).isSameOrBefore(moment());
      this.planDayPartDayForm.patchValue(this.data.dayPartDay);
    }

    if (this.data.planType == 'drivers') {
      this.planDayPartDayForm.addControl(
        'driverId',
        new UntypedFormControl('', Validators.required),
      );
      if (dataStore.generalSettings.coDriverEnabled) {
        this.planDayPartDayForm.addControl('coDriverId', new UntypedFormControl(''));
      }
      this.planDayPartDoc = doc(
        db,
        `organisations/${this.organisationId}/daypartDays/${this.startString}.${this.data.dayPart.id}.${this.data.vehicle.id}`,
      );
      await this.getPlanningUsers('driver');
      if (this.data && this.data.dayPartDay) {
        await this.changedUser(this.data.dayPartDay.driverId, 'driver');
        this.planDayPartDayForm.patchValue(this.data.dayPartDay);
      }
      this.planDayPartDayForm.controls.driverId.valueChanges.subscribe((driverId) => {
        this.getCurrentUser(driverId, 'driver');
        this.updatePrivateVehicleDriverValidation();
      });
      if (dataStore.generalSettings.coDriverEnabled) {
        if (this.data.dayPartDay?.coDriverId) {
          await this.changedUser(this.data.dayPartDay.coDriverId, 'co-driver');
        }
        this.planDayPartDayForm.controls.coDriverId.valueChanges.subscribe((coDriverId) => {
          this.getCurrentUser(coDriverId, 'co-driver');
        });
      }
      this.initializePrivateVehicleCrew();
    } else if (this.data.planType == 'planners') {
      this.planDayPartDayForm.addControl(
        'plannerIds',
        new UntypedFormControl([], Validators.required),
      );
      this.planDayPartDoc = doc(
        db,
        `organisations/${this.organisationId}/daypartDays/${this.startString}.${this.data.dayPart.id}`,
      );
      await this.getPlanningUsers('planner');
      if (this.data && this.data.dayPartDay?.plannerIds) {
        this.selectPlanner(this.data.dayPartDay.plannerIds);
        this.planDayPartDayForm.controls.plannerIds.setValue(this.data.dayPartDay.plannerIds);
      }
    }

    if (this.isFinished) {
      this.planDayPartDayForm.disable({ emitEvent: false });
      this.searchForm.disable({ emitEvent: false });
    }

    this.planDayPartDayForm.controls.customStart.valueChanges.subscribe((value) => {
      if (value) {
        this.planDayPartDayForm.controls.startHour.setValidators(Validators.required);
        this.planDayPartDayForm.controls.startMinute.setValidators(Validators.required);
        this.planDayPartDayForm.updateValueAndValidity();
      } else {
        this.planDayPartDayForm.controls.startHour.clearValidators();
        this.planDayPartDayForm.controls.startMinute.clearValidators();
        this.planDayPartDayForm.updateValueAndValidity();
      }
    });
    const timeObj = {
      startHour: moment.tz(valueToDate(this.data.start), 'Europe/Amsterdam').hour(),
      startMinute: moment.tz(valueToDate(this.data.start), 'Europe/Amsterdam').minute(),
      endHour: moment.tz(valueToDate(this.data.end), 'Europe/Amsterdam').hour(),
      endMinute: moment.tz(valueToDate(this.data.end), 'Europe/Amsterdam').minute(),
    };
    this.planDayPartDayForm.patchValue(timeObj);

    this.checkTimeCombination();
    this.planDayPartDayForm.valueChanges.subscribe(() => {
      this.checkTimeCombination();
      this.updateUsersBookingStatus();
      this.updatePrivateVehicleDriverValidation();
    });

    this.loading.set(false);
  }

  ngOnDestroy(): void {
    if (this.daypartDaysSub) {
      this.daypartDaysSub();
    }
  }

  checkTimeCombination() {
    const values = this.planDayPartDayForm.value;
    if (
      values.startHour != null &&
      values.startMinute != null &&
      values.endHour != null &&
      values.endMinute != null
    ) {
      const startTime = moment().set({
        hour: values.startHour,
        minute: values.startMinute,
        second: 0,
      });
      const endTime = moment().set({
        hour: values.endHour,
        minute: values.endMinute,
        second: 0,
      });
      this.invalidTimeCombination.set(startTime.isSameOrAfter(endTime));
    } else {
      // When not all time fields are filled in, do not treat the combination as invalid.
      this.invalidTimeCombination.set(false);
    }
  }

  async getPlanningUsers(role: string) {
    const queryConstraints: QueryConstraint[] = [];
    if (role === 'driver' && dataStore.generalSettings.coDriverEnabled) {
      queryConstraints.push(where('roles', 'array-contains-any', [Role.Driver, Role.CoDriver]));
    } else if (role === 'planner') {
      queryConstraints.push(where('roles', 'array-contains-any', [Role.Planner, Role.RidePlanner]));
    } else {
      queryConstraints.push(where('roles', 'array-contains', role));
    }

    const roleEnum =
      role === 'driver' ? Role.Driver : role === 'co-driver' ? Role.CoDriver : Role.Planner;

    const userDocs = await getDocs(
      query(collection(db, `organisations/${this.organisationId}/users`), ...queryConstraints),
    );
    const allUsers = [];
    userDocs.forEach((userDoc) => {
      const user = userDoc.data() as User;
      const planningUser: PlanningUser = {
        id: userDoc.id,
        user: user,
        priority: 0,
      };
      if (this.data.dayPart.availableUsers) {
        const available = this.data.dayPart.availableUsers.find(
          (userRef) => userRef.id === userDoc.ref.id,
        );
        if (available) {
          planningUser.available = true;
          planningUser.priority = planningUser.priority + 1;
        }
      }
      if (user.exceptions) {
        const exception = user.exceptions?.find((exception) => {
          const excFrom = valueToDate(exception.dateFrom);
          const excTo = valueToDate(exception.dateTo);
          return moment(excFrom).isBefore(this.data.end) && moment(excTo).isAfter(this.data.start);
        });
        if (exception) {
          planningUser.hasException = true;
          planningUser.exceptionTitle = exception.name;
          planningUser.priority = -1;
        }
      }
      allUsers.push(this.augmentPlanningUser(planningUser, roleEnum));
    });
    allUsers.sort((a, b) => {
      if (a.priority < b.priority) {
        return 1;
      } else if (a.priority === b.priority && a.user.name > b.user.name) {
        return 1;
      } else {
        return -1;
      }
    });
    this.allUsers = [...allUsers];

    this.filteredUsers.set(this.getFilteredUsers(role, ''));
    this.searchForm.controls.search.valueChanges.subscribe((searchQuery) => {
      this.filteredUsers.set(this.getFilteredUsers(role, searchQuery));
      // if (this.data.planType === 'drivers' && this.planDayPartDayForm.valid) {
      //   this.changedDriver(this.planDayPartDayForm.value.driverId);
      // }
    });
    if (dataStore.generalSettings.coDriverEnabled) {
      this.filteredCoDrivers.set(this.getFilteredUsers(Role.CoDriver, ''));
      this.searchForm.controls.searchCoDriver.valueChanges.subscribe((searchQuery) => {
        this.filteredCoDrivers.set(this.getFilteredUsers(Role.CoDriver, searchQuery));
        // if (this.data.planType === 'drivers' && this.planDayPartDayForm.valid) {
        //   this.changedCoDriver(this.planDayPartDayForm.value.coDriverId);
        // }
      });
    }

    if (this.daypartDaysSub) {
      this.daypartDaysSub();
    }
    const q = query(
      collection(db, `organisations/${this.organisationId}/daypartDays`),
      where('day', '==', this.date.getDate()),
      where('month', '==', this.date.getMonth()),
      where('year', '==', this.date.getFullYear()),
    );
    this.daypartDaysSub = onSnapshot(q, (snapshot) => {
      this.daypartDaysForDate = snapshot.docs
        .filter((doc) => doc.id !== this.planDayPartDoc.id)
        .map((doc) => doc.data() as DaypartDay);
      this.updateUsersBookingStatus();
    });
  }

  private augmentPlanningUser(u: PlanningUser, role: Role): PlanningUser {
    const vehicles = role === Role.CoDriver ? u.user.vehiclesToCoRide : u.user.vehiclesToRide;
    const assignedToPrivateVehicle =
      this.data.vehicle?.vehicleType === 'private' &&
      (role === Role.CoDriver
        ? u.id === this.data.vehicle.coDriverId
        : u.id === this.data.vehicle.driverId);
    const ridesVehicle =
      assignedToPrivateVehicle ||
      !!(this.data.vehicle && vehicles && vehicles.includes(this.data.vehicle.id));
    const recommended =
      (this.data.planType === 'planners' || ridesVehicle) &&
      u.available &&
      !u.hasException &&
      !u.alreadyBooked;
    const priority = recommended ? 1 : u.hasException ? -1 : 0;
    return {
      ...u,
      ridesVehicle,
      recommended,
      priority,
      alreadyBooked: u.alreadyBooked,
    } as PlanningUser;
  }

  initializePrivateVehicleCrew(): void {
    if (this.data.vehicle?.vehicleType !== 'private') {
      return;
    }

    const fixedDriver = this.allUsers.find(
      (user) => user.id === this.data.vehicle.driverId && user.user.roles?.includes(Role.Driver),
    );
    const fixedCoDriver = this.allUsers.find(
      (user) =>
        user.id === this.data.vehicle.coDriverId && user.user.roles?.includes(Role.CoDriver),
    );
    const hasExistingDaypart =
      !!this.data.dayPartDay && Object.keys(this.data.dayPartDay).length > 0;
    const existingCoDriver = hasExistingDaypart
      ? this.allUsers.find(
          (user) =>
            user.id === this.data.dayPartDay.coDriverId && user.user.roles?.includes(Role.CoDriver),
        )
      : undefined;
    const historicalDriver = hasExistingDaypart
      ? this.allUsers.find(
          (user) =>
            user.id === this.data.dayPartDay.driverId && user.user.roles?.includes(Role.Driver),
        )
      : undefined;
    const selectedDriver = historicalDriver ?? fixedDriver;
    const selectedCoDriver = hasExistingDaypart ? existingCoDriver : fixedCoDriver;
    const values: Record<string, string> = {
      driverId: selectedDriver?.id ?? 'none',
    };
    if (this.planDayPartDayForm.contains('coDriverId')) {
      values.coDriverId = selectedCoDriver?.id ?? 'none';
    }
    this.planDayPartDayForm.patchValue(values, { emitEvent: false });
    this.currentUser.set(
      selectedDriver ? this.augmentPlanningUser(selectedDriver, Role.Driver) : null,
    );
    this.currentCoDriver.set(
      selectedCoDriver ? this.augmentPlanningUser(selectedCoDriver, Role.CoDriver) : null,
    );
    this.updatePrivateVehicleDriverValidation();
  }

  updatePrivateVehicleDriverValidation(): void {
    if (this.data.vehicle?.vehicleType !== 'private') {
      this.privateVehicleDriverError.set(null);
      return;
    }

    const selectedDriverId = this.planDayPartDayForm.controls.driverId?.value;
    if (!selectedDriverId || selectedDriverId === 'none') {
      this.privateVehicleDriverError.set(null);
      return;
    }
    if (selectedDriverId !== this.data.vehicle.driverId) {
      this.privateVehicleDriverError.set(
        'Alleen de vaste chauffeur van dit privévoertuig kan worden geselecteerd.',
      );
      return;
    }

    const fixedDriver = this.allUsers?.find((user) => user.id === selectedDriverId);
    if (!fixedDriver?.user.roles?.includes(Role.Driver)) {
      this.privateVehicleDriverError.set(
        'De vaste chauffeur van dit privévoertuig is niet beschikbaar.',
      );
      return;
    }
    this.privateVehicleDriverError.set(null);
  }

  updateUsersBookingStatus() {
    const form = this.planDayPartDayForm.value;
    if (
      form.startHour == null ||
      form.startMinute == null ||
      form.endHour == null ||
      form.endMinute == null
    ) {
      return;
    }
    const start = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.startHour, minute: form.startMinute });
    const end = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.endHour, minute: form.endMinute });

    if (!this.allUsers) {
      return;
    }

    for (const u of this.allUsers) {
      let alreadyBooked = false;
      for (const daypartDay of this.daypartDaysForDate) {
        const userInvolved =
          daypartDay.driverId === u.id ||
          daypartDay.coDriverId === u.id ||
          (daypartDay.plannerIds && daypartDay.plannerIds.includes(u.id));

        if (userInvolved) {
          const existingStart = moment(valueToDate(daypartDay.start));
          const existingEnd = moment(valueToDate(daypartDay.end));

          if (start.isBefore(existingEnd) && existingStart.isBefore(end)) {
            alreadyBooked = true;
            break;
          }
        }
      }
      u.alreadyBooked = alreadyBooked;
    }

    const role = this.data.planType === 'drivers' ? 'driver' : 'planner';
    const searchVal = this.searchForm.controls.search.value || '';
    this.filteredUsers.set(this.getFilteredUsers(role, searchVal));
    if (dataStore.generalSettings.coDriverEnabled) {
      const searchCoVal = this.searchForm.controls.searchCoDriver.value || '';
      this.filteredCoDrivers.set(this.getFilteredUsers(Role.CoDriver, searchCoVal));
    }

    if (this.planDayPartDayForm.controls.driverId) {
      const driverId = this.planDayPartDayForm.controls.driverId.value;
      if (driverId && driverId !== 'none') {
        this.getCurrentUser(driverId, 'driver');
      }
    }
    if (dataStore.generalSettings.coDriverEnabled && this.planDayPartDayForm.controls.coDriverId) {
      const coDriverId = this.planDayPartDayForm.controls.coDriverId.value;
      if (coDriverId && coDriverId !== 'none') {
        this.getCurrentUser(coDriverId, 'co-driver');
      }
    }
    if (this.data.planType === 'planners' && this.planDayPartDayForm.controls.plannerIds) {
      const plannerIds = this.planDayPartDayForm.controls.plannerIds.value || [];
      if (plannerIds.indexOf('none') === -1) {
        const updatedSelected: PlanningUser[] = [];
        plannerIds.forEach((plannerId) => {
          const found = this.filteredUsers().find((user) => user.id === plannerId);
          if (found) {
            updatedSelected.push(found);
          }
        });
        this.selectedPlannerUsers.set(updatedSelected);
      }
    }
  }

  getFilteredUsers(role: string, searchQuery: string): PlanningUser[] {
    const filteredUsers = this.allUsers
      .filter((u) => {
        const isPrivateVehicleDriver =
          this.data.vehicle?.vehicleType === 'private' && role === Role.Driver;
        const isHistoricalDriver =
          isPrivateVehicleDriver && u.id === this.data.dayPartDay?.driverId;
        return (
          u.user.name &&
          u.user.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          (role === 'planner'
            ? u.user.roles?.includes(Role.Planner) || u.user.roles?.includes(Role.RidePlanner)
            : u.user.roles?.includes(role as Role)) &&
          (this.data.planType !== 'drivers' || !isPrivateVehicleDriver
            ? true
            : u.id === this.data.vehicle.driverId || isHistoricalDriver)
        );
      })
      .map((u) => this.augmentPlanningUser(u, role as Role));

    filteredUsers.sort((a, b) => {
      if (a.priority < b.priority) {
        return 1;
      } else if (a.priority === b.priority && a.user.name > b.user.name) {
        return 1;
      } else {
        return -1;
      }
    });
    return filteredUsers;
  }

  isHistoricalPrivateVehicleDriverChanged(): boolean {
    return (
      this.data.vehicle?.vehicleType === 'private' &&
      !!this.data.dayPartDay &&
      Object.keys(this.data.dayPartDay).length > 0 &&
      !!this.data.dayPartDay.driverId &&
      this.data.dayPartDay.driverId !== this.data.vehicle.driverId
    );
  }

  isHistoricalPrivateVehicleDriverDisabled(userId: string): boolean {
    return (
      this.isHistoricalPrivateVehicleDriverChanged() && userId === this.data.dayPartDay?.driverId
    );
  }

  getCurrentUser(userId: string, role: 'driver' | 'co-driver') {
    const userFound = this.allUsers?.find((user) => user.id === userId);
    if (role === 'driver') {
      const user = userFound ? this.augmentPlanningUser(userFound, Role.Driver) : null;
      this.currentUser.set(user);
    } else {
      const user = userFound ? this.augmentPlanningUser(userFound, Role.CoDriver) : null;
      this.currentCoDriver.set(user);
    }
  }

  getSelectedUser(role: 'driver' | 'co-driver'): PlanningUser | null {
    const controlName = role === 'driver' ? 'driverId' : 'coDriverId';
    const selectedUserId = this.planDayPartDayForm.controls[controlName]?.value;
    if (!selectedUserId || selectedUserId === 'none') {
      return null;
    }
    const selectedUser = this.allUsers?.find((user) => user.id === selectedUserId);
    return selectedUser
      ? this.augmentPlanningUser(selectedUser, Role[role === 'driver' ? 'Driver' : 'CoDriver'])
      : null;
  }

  async changedUser(userId: string, role: 'driver' | 'co-driver') {
    const userDoc = await getDoc(doc(db, `organisations/${this.organisationId}/users/${userId}`));
    const propertyPrefix = role === 'driver' ? 'driver' : 'coDriver';
    const userData = userDoc.data();
    this[`${propertyPrefix}Email`] = '';
    this[`${propertyPrefix}Phone`] = '';
    this.getCurrentUser(userId, role);
    if (!userData) {
      return;
    }
    if (userData.email != null) {
      this[`${propertyPrefix}Email`] = userData.email;
    }
    if (userData.phoneNumber != null) {
      this[`${propertyPrefix}Phone`] = userData.phoneNumber;
    } else if (userData.phone != null) {
      this[`${propertyPrefix}Phone`] = userData.phone;
    }
  }

  selectPlanner(plannerIds) {
    if (plannerIds.indexOf('none') !== -1) {
      plannerIds.splice(plannerIds.indexOf('none'), 1);
      plannerIds.forEach((planner) => {
        this.removePlanner(planner);
      });
      this.selectedPlannerUsers.set([]);
      this.planDayPartDayForm.controls.plannerIds.setValue(['none']);
    } else {
      this.selectedPlannerIds.set(plannerIds);
      this.selectedPlannerUsers.set([]);
      plannerIds.forEach((plannerId) => {
        this.filteredUsers().forEach((user) => {
          if (user.id == plannerId) {
            this.selectedPlannerUsers.update((users) => [...users, user]);
          }
        });
      });
    }
  }

  removePlanner(planner) {
    if (this.isFinished) {
      return;
    }
    const index = this.selectedPlannerIds().indexOf(planner);
    if (index !== -1) {
      const spliced = this.selectedPlannerIds()[index];
      this.removedPlannerIds.update((ids) => [...ids, spliced]);
      this.selectedPlannerIds.update((ids) => ids.filter((_, i) => i !== index));
    }
    this.selectPlanner(this.selectedPlannerIds());
    this.planDayPartDayForm.controls.plannerIds.setValue(this.selectedPlannerIds());
  }

  restorePlannerUsers() {
    if (this.data.planType == 'planners' && this.removedPlannerIds().length > 0) {
      if (!this.data.dayPartDay.plannerIds) {
        this.data.dayPartDay.plannerIds = [];
      }
      this.removedPlannerIds().forEach((id) => {
        this.data.dayPartDay.plannerIds.push(id);
      });
    }
  }

  async savePlanner() {
    if (this.isFinished || this.saving()) {
      return;
    }
    this.checkTimeCombination();
    if (!this.planDayPartDayForm.valid || this.invalidTimeCombination()) {
      this.planDayPartDayForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);

    const form = this.planDayPartDayForm.value;
    if (form.plannerIds.length === 0 || form.plannerIds.indexOf('none') != -1) {
      await deleteDoc(this.planDayPartDoc);
      return this.dialogRef.close();
    }
    const plannersNames = [];
    const plannersIds = [];
    this.selectedPlannerUsers().forEach(async (user) => {
      plannersNames.push(user.user.name);
      plannersIds.push(user.id);
    });
    this.saveItem.planners = plannersNames;
    this.saveItem.plannerIds = plannersIds;
    this.saveItem.lastUpdated = new Date();
    this.saveItem.customStart = form.customStart;
    this.saveItem.customEnd = form.customEnd;
    this.saveItem.startHour = form.startHour;
    this.saveItem.startMinute = form.startMinute;
    this.saveItem.endHour = form.endHour;
    this.saveItem.endMinute = form.endMinute;
    this.saveItem.comment = form.comment;
    this.saveItem.type = 'planner';
    if (this.data.dayPartDay?.startHour != form.startHour || this.data.dayPartDay?.customStart) {
      this.saveItem.customStart = true;
    }
    if (this.data.dayPartDay?.endHour != form.endHour || this.data.dayPartDay?.customEnd) {
      this.saveItem.customEnd = true;
    }
    this.saveItem.start = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.startHour, minute: form.startMinute })
      .toDate();
    this.saveItem.end = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.endHour, minute: form.endMinute })
      .toDate();

    Object.keys(this.saveItem).forEach((key) => {
      if (this.saveItem[key] == null || this.saveItem[key] == undefined) {
        delete this.saveItem[key];
      }
    });

    const bookedPlanners = await this.checkIfPlannerAlreadyBooked(
      this.selectedPlannerUsers(),
      this.saveItem.start,
      this.saveItem.end,
    );

    let proceed = true;
    // Hard block first: already booked takes priority over soft planning warnings.
    if (proceed && bookedPlanners.length > 0) {
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: {
          dialogType: 'plannerAlreadyBookedInDaypart',
          optionalData: { plannerName: bookedPlanners[0].user.name },
        },
      });
      proceed = false;
    }

    // Soft warnings after hard block.
    for (const planner of this.selectedPlannerUsers()) {
      if (proceed) {
        proceed = await this.checkUserPlanningWarnings(
          planner,
          planner.user.roles?.includes(Role.RidePlanner) ? Role.RidePlanner : Role.Planner,
        );
      }
    }

    if (proceed) {
      await setDoc(this.planDayPartDoc, this.saveItem, { merge: true });
      this.removedPlannerIds.set([]);
      this.dialogRef.close();
    } else {
      this.saving.set(false);
    }
  }

  async checkIfPlannerAlreadyBooked(planners: any, startDate: Date, endDate: Date) {
    const alreadyBooked = [];
    for (const user of planners) {
      const isBooked = await this.checkIfEmployeeAlreadyBooked(
        'planner',
        user.id,
        startDate,
        endDate,
      );
      if (isBooked) {
        alreadyBooked.push(user);
      }
    }
    return alreadyBooked;
  }

  async saveDriver() {
    if (this.isFinished || this.saving()) {
      return;
    }
    this.checkTimeCombination();
    if (!this.planDayPartDayForm.valid || this.invalidTimeCombination()) {
      this.planDayPartDayForm.markAllAsTouched();
      return;
    }
    const form = this.planDayPartDayForm.value;
    if (this.data.vehicle?.vehicleType === 'private') {
      this.updatePrivateVehicleDriverValidation();
      if (form.driverId !== 'none' && this.privateVehicleDriverError()) {
        this.snackBar.open(this.privateVehicleDriverError()!, 'X', {
          duration: 5000,
        });
        return;
      }
      const historicalDriverId = this.isHistoricalPrivateVehicleDriverChanged()
        ? this.data.dayPartDay?.driverId
        : undefined;
      if (
        form.driverId !== 'none' &&
        form.driverId !== this.data.vehicle.driverId &&
        form.driverId !== historicalDriverId
      ) {
        this.snackBar.open(
          'Alleen de vaste chauffeur van dit privévoertuig kan worden geselecteerd.',
          'X',
          { duration: 5000 },
        );
        return;
      }
    }
    this.saving.set(true);

    if (form.driverId === 'none') {
      await deleteDoc(this.planDayPartDoc);
      return this.dialogRef.close();
    }
    const selectedDriver = this.allUsers.find((user) => user.id === form.driverId);
    this.saveItem.driverId = form.driverId;
    this.saveItem.driverName = selectedDriver.user.name;
    this.saveItem.lastUpdated = new Date();
    this.saveItem.customStart = form.customStart;
    this.saveItem.customEnd = form.customEnd;
    this.saveItem.startHour = form.startHour;
    this.saveItem.startMinute = form.startMinute;
    this.saveItem.endHour = form.endHour;
    this.saveItem.endMinute = form.endMinute;
    this.saveItem.comment = form.comment;
    this.saveItem.type = 'driver';
    if (form.coDriverId && form.coDriverId !== 'none') {
      const selectedCoDriver = this.allUsers.find((user) => user.id === form.coDriverId);
      this.saveItem.coDriverId = form.coDriverId;
      this.saveItem.coDriverName = selectedCoDriver.user.name;
    } else {
      this.saveItem.coDriverId = deleteField();
      this.saveItem.coDriverName = deleteField();
    }

    if (this.data.dayPartDay?.startHour != form.startHour || this.data.dayPartDay?.customStart) {
      this.saveItem.customStart = true;
    }
    if (this.data.dayPartDay?.endHour != form.endHour || this.data.dayPartDay?.customEnd) {
      this.saveItem.customEnd = true;
    }
    this.saveItem.start = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.startHour, minute: form.startMinute })
      .toDate();
    this.saveItem.end = moment
      .tz(this.date, 'Europe/Amsterdam')
      .set({ hour: form.endHour, minute: form.endMinute })
      .toDate();

    Object.keys(this.saveItem).forEach((key) => {
      if (this.saveItem[key] == null || this.saveItem[key] == undefined) {
        delete this.saveItem[key];
      }
    });

    let proceed = true;
    // Hard blocks first: check double booking before soft planning warnings.
    if (proceed) {
      proceed = await this.checkDoubleBooking();
    }
    if (proceed && form.driverId && form.driverId !== 'none') {
      const selectedDriver = this.allUsers.find((user) => user.id === form.driverId);
      if (selectedDriver) {
        proceed = await this.checkUserPlanningWarnings(selectedDriver, Role.Driver);
      }
    }
    if (proceed && form.coDriverId && form.coDriverId !== 'none') {
      proceed = await this.checkDoubleBooking('coDriver');
    }
    if (proceed && form.coDriverId && form.coDriverId !== 'none') {
      const coDriverSeatsAvailable = await this.checkCoDriverSeatAvailable();
      if (!coDriverSeatsAvailable && proceed) {
        proceed = await new Promise((resolve) => {
          const noSeatsDialogRef = this.dialog.open(WarningDialog, {
            width: '600px',
            data: { dialogType: 'noCoDriverSeatsAvailable' },
          });
          noSeatsDialogRef.afterClosed().subscribe((result) => {
            resolve(result);
          });
        });
      }
      if (proceed) {
        const selectedCoDriver = this.allUsers.find((user) => user.id === form.coDriverId);
        if (selectedCoDriver) {
          proceed = await this.checkUserPlanningWarnings(selectedCoDriver, Role.CoDriver);
        }
      }
    }

    if (proceed) {
      await setDoc(this.planDayPartDoc, this.saveItem, { merge: true });
      this.dialogRef.close();
    } else {
      this.saving.set(false);
    }
  }

  private getRoleDutch(role: Role): string {
    switch (role) {
      case Role.Driver:
        return 'Chauffeur';
      case Role.CoDriver:
        return 'Bijrijder';
      case Role.Planner:
        return 'Planner';
      case Role.RidePlanner:
        return 'Ritplanner';
      default:
        return 'Medewerker';
    }
  }

  async checkUserPlanningWarnings(
    user: PlanningUser | undefined | null,
    role: Role,
  ): Promise<boolean> {
    if (!user) {
      return true;
    }
    const warnings: string[] = [];

    if (!user.available) {
      warnings.push('Onbeschikbaar op dit tijdstip');
    }

    let ridesVehicle = user.ridesVehicle;
    if (this.data.vehicle) {
      if (
        this.data.vehicle.vehicleType === 'private' &&
        (role === Role.Driver
          ? user.id === this.data.vehicle.driverId
          : user.id === this.data.vehicle.coDriverId)
      ) {
        ridesVehicle = true;
      } else if (role === Role.Driver) {
        ridesVehicle = user.user.vehiclesToRide?.includes(this.data.vehicle.id);
      } else if (role === Role.CoDriver) {
        ridesVehicle = user.user.vehiclesToCoRide?.includes(this.data.vehicle.id);
      }
    }

    if ((role === Role.Driver || role === Role.CoDriver) && !ridesVehicle) {
      warnings.push('Onbeschikbaar voor dit voertuig');
    }
    if (user.hasException) {
      const message = user.exceptionTitle
        ? `Uitzondering: ${user.exceptionTitle}`
        : 'Heeft een uitzondering op dit tijdstip';
      warnings.push(message);
    }

    if (warnings.length > 0) {
      return new Promise((resolve) => {
        const warningDialogRef = this.dialog.open(WarningDialog, {
          width: '600px',
          data: {
            dialogType: 'userPlanningWarnings',
            optionalData: {
              role: this.getRoleDutch(role),
              userName: user.user.name,
              warnings: warnings,
            },
          },
        });
        warningDialogRef.afterClosed().subscribe((result) => {
          resolve(result);
        });
      });
    }

    return true;
  }

  private async checkDoubleBooking(type: string = 'driver'): Promise<boolean> {
    const alreadyBooked = await this.checkIfEmployeeAlreadyBooked(
      type,
      type === 'driver' ? this.saveItem.driverId : this.saveItem.coDriverId,
      this.saveItem.start,
      this.saveItem.end,
    );

    if (alreadyBooked) {
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: {
          dialogType:
            type === 'driver' ? 'driverAlreadyBookedInDaypart' : 'coDriverAlreadyBookedInDaypart',
        },
      });
      return false;
    }

    return true;
  }

  async checkIfEmployeeAlreadyBooked(type: string, userId: string, startDate: Date, endDate: Date) {
    const start = moment(startDate);
    const end = moment(endDate);

    // Check all possible role fields for this user using separate queries to be safe
    const baseQuery = collection(db, `organisations/${this.organisationId}/daypartDays`);
    const dateFilters = [
      where('day', '==', start.date()),
      where('month', '==', start.month()),
      where('year', '==', start.year()),
    ];

    const queries = [
      query(baseQuery, ...dateFilters, where('driverId', '==', userId)),
      query(baseQuery, ...dateFilters, where('coDriverId', '==', userId)),
      query(baseQuery, ...dateFilters, where('plannerIds', 'array-contains', userId)),
    ];

    const results = await Promise.all(queries.map((q) => getDocs(q)));
    const allDocs = results.flatMap((snapshot) => snapshot.docs);

    let alreadyBooked = false;
    for (const daypartDayDoc of allDocs) {
      if (daypartDayDoc.id === this.planDayPartDoc.id) continue;

      const daypartDay = daypartDayDoc.data() as DaypartDay;
      const existingStart = moment(valueToDate(daypartDay.start));
      const existingEnd = moment(valueToDate(daypartDay.end));

      if (start.isBefore(existingEnd) && existingStart.isBefore(end)) {
        alreadyBooked = true;
        break;
      }
    }
    return alreadyBooked;
  }

  async checkCoDriverSeatAvailable(): Promise<boolean> {
    const rides = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/rides`),
          where('day', '==', moment(this.saveItem.start).date()),
          where('month', '==', moment(this.saveItem.start).month()),
          where('year', '==', moment(this.saveItem.start).year()),
          where('vehicleId', '==', this.data.vehicle.id),
        ),
      )
    ).docs.map((doc) => {
      return doc.data() as Ride;
    });

    // Filter rides that overlap with the daypart time
    const overlappingRides = rides.filter((ride) => {
      return moment(valueToDate(ride.start)).isBetween(
        moment(this.saveItem.start),
        moment(this.saveItem.end),
        null,
        '[)',
      );
    });

    const candidateCoDriverId =
      typeof this.saveItem.coDriverId === 'string' ? this.saveItem.coDriverId : null;
    const unavailableRides = overlappingRides.filter((ride) => {
      if (!ride.automaticallyPlanned) {
        return ride.availableSeats < 1;
      }

      const occupancy = calculateAutomaticRideOccupancy(
        ride.locationsOrder ?? [],
        this.data.vehicle.seats,
        ride.coDriverId ?? candidateCoDriverId,
        Object.values(ride.users ?? {}),
        dataStore.selectedOrganisation.particularities ?? [],
      );
      return occupancy.some((timeSlot) => timeSlot.availableSeats < 0);
    });

    if (unavailableRides.length > 0) {
      console.log('No seats for codriver in following rides:', unavailableRides);
    }
    return unavailableRides.length === 0;
  }

  navigateToUser(id: string, role: string) {
    window.open(`${window.location.origin}/schedule/${role}/${id}`, '_blank');
  }

  getSelectedPlannerNames(): string {
    if (this.selectedPlannerUsers().length > 0) {
      return this.selectedPlannerUsers()
        .map((p) => p.user.name)
        .join(', ');
    } else {
      return 'Geen';
    }
  }

  getUserInfoTooltip(role: Role, message: string): string {
    switch (message) {
      case 'available':
        return 'Beschikbaar op dit tijdstip';
      case 'unavailable':
        return 'Onbeschikbaar op dit tijdstip';
      case 'alreadyBooked':
        return 'Al ingepland op dit tijdstip';
      case 'notRecommended':
        return 'Het wordt afgeraden om deze medewerker in te plannen.';
      case 'vehicle':
        return 'Beschikbaar voor dit voertuig';
      case 'noVehicle':
        return 'Onbeschikbaar voor dit voertuig';
      default:
        return '';
    }
  }

  openCopyDialog(type: string) {
    let data: any;
    if (type == 'driver') {
      const coDriverId = this.planDayPartDayForm.value.coDriverId;
      let coDriverName = null;
      if (coDriverId && coDriverId !== 'none') {
        coDriverName = this.allUsers.find((u) => u.id === coDriverId)?.user.name;
      }
      data = {
        type: type,
        daypartData: this.data,
        driverId: this.planDayPartDayForm.value.driverId,
        coDriverId: coDriverId,
        coDriverName: coDriverName,
        vehicleId: this.data.vehicle.id,
      };
    } else if (type == 'planner') {
      const plannersNames = [];
      const plannersIds = [];
      this.selectedPlannerUsers().forEach(async (user) => {
        plannersNames.push(user.user.name);
        plannersIds.push(user.id);
      });
      data = {
        type: type,
        daypartData: this.data,
        plannerIds: plannersIds,
        planners: plannersNames,
      };
    }
    const dialogRef = this.dialog.open(CopyDaypartComponent, {
      width: '535px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('result', result);
    });
  }
}
